import { estimatePlayedPriceCents, getVariantPriceCents } from '../../../utils/price'
import { CONDITION_ORDER, PRINTING_ORDER, STANDARD_CONDITIONS } from '../../../utils/variantOrdering'
import type { TradeItem, TradeVariant } from './types'
import { localStorageKeys } from '../../../constants/storage'

export {
  CONDITION_ORDER,
  PRINTING_ORDER,
  STANDARD_CONDITIONS,
  UUID_PATTERN,
} from '../../../utils/variantOrdering'

export const OTHER_CONDITION_AFFORDANCE = 'Other (set price)'

export function enrichVariantsForTrade(variants: TradeVariant[]): TradeVariant[] {
  const enriched = [...variants]
  const existingCombos = new Set(
    enriched.map((v) => `${v.printing || 'Normal'}|${v.condition || 'Near Mint'}`),
  )

  const printingsFromApi = [...new Set(variants.map((v) => v.printing || 'Normal'))]
  const printings = printingsFromApi.length > 0 ? printingsFromApi : ['Normal']

  /** Only the primary printing gets synthetic standard-condition rows; other printings stay API-only. */
  const primaryPrinting =
    [...printings].sort((a, b) => (PRINTING_ORDER[a] ?? 99) - (PRINTING_ORDER[b] ?? 99))[0] ?? 'Normal'

  const slug = (s: string) => s.toLowerCase().replace(/\s+/g, '-')

  for (const cond of STANDARD_CONDITIONS) {
    const key = `${primaryPrinting}|${cond}`
    if (existingCombos.has(key)) continue
    enriched.push({
      id: `${slug(primaryPrinting)}_${slug(cond)}_synth`,
      printing: primaryPrinting,
      condition: cond,
      price: 0,
      priceCents: 0,
    })
    existingCombos.add(key)
  }

  const otherKey = `${primaryPrinting}|${OTHER_CONDITION_AFFORDANCE}`
  if (!existingCombos.has(otherKey)) {
    enriched.push({
      id: `${slug(primaryPrinting)}_other-manual`,
      printing: primaryPrinting,
      condition: OTHER_CONDITION_AFFORDANCE,
      price: 0,
      priceCents: 0,
    })
    existingCombos.add(otherKey)
  }

  // An unpriced played condition gets an ESTIMATE from its printing's Near Mint price. This used
  // to copy the nearest priced condition (usually Near Mint) into a fallback that the price
  // readers folded in, so a damaged trade-in was valued at the near-mint price.
  for (const v of enriched) {
    if (getVariantPriceCents(v) > 0) continue
    const nearMint = enriched.find(
      (o) => o.printing === v.printing && CONDITION_ORDER[o.condition || ''] === 1 && getVariantPriceCents(o) > 0,
    )
    const estimate = nearMint ? estimatePlayedPriceCents(getVariantPriceCents(nearMint), v.condition) : null
    if (estimate !== null) v.estimatedPriceCents = estimate
  }

  enriched.sort((a, b) => {
    const pA = PRINTING_ORDER[a.printing || ''] ?? 99
    const pB = PRINTING_ORDER[b.printing || ''] ?? 99
    if (pA !== pB) return pA - pB
    const cA = CONDITION_ORDER[a.condition || ''] ?? 99
    const cB = CONDITION_ORDER[b.condition || ''] ?? 99
    return cA - cB
  })

  return enriched
}

export function findDefaultVariant(variants: TradeVariant[]): TradeVariant | undefined {
  return (
    variants.find(
      (v) =>
        v.condition === 'Near Mint' &&
        v.printing === 'Normal' &&
        (v.price > 0 || v.priceCents > 0),
    ) ||
    variants.find((v) => v.condition === 'Near Mint' && (v.price > 0 || v.priceCents > 0)) ||
    variants.find((v) => v.price > 0 || v.priceCents > 0) ||
    variants.find((v) => v.condition === 'Near Mint' && v.printing === 'Normal') ||
    variants[0]
  )
}

export const activeTradeStorageKey = localStorageKeys.activeTradeId
export const tradePaymentMethodStorageKey = localStorageKeys.tradePaymentMethod

export function formatDraftTradeRef(tradeId: string): string {
  const compact = tradeId.replace(/-/g, '')
  return `${compact.slice(0, 8)}…${compact.slice(-4)}`.toUpperCase()
}

/** Canonical string for an issued receipt number (pair with `formatDraftTradeRef` for drafts). */
export function formatReceiptNumber(receiptNumber: number): string {
  return String(Math.trunc(receiptNumber))
}

type TradeRefSource = { id: string; receiptNumber?: number | null }

/** In-progress trade header: official receipt number or short draft ref (never a raw UUID). */
export function formatActiveTradeRefLabel(trade: TradeRefSource): string {
  if (typeof trade.receiptNumber === 'number' && trade.receiptNumber > 0) {
    return `Receipt #${formatReceiptNumber(trade.receiptNumber)}`
  }
  return `Draft ref ${formatDraftTradeRef(trade.id)}`
}

/** Completed receipt banner line — same receipt vs draft ref distinction as the trade builder. */
export function formatCompletedTradeRefLabel(trade: TradeRefSource): string {
  if (typeof trade.receiptNumber === 'number' && trade.receiptNumber > 0) {
    return `Receipt #${formatReceiptNumber(trade.receiptNumber)}`
  }
  return `Transaction ref ${formatDraftTradeRef(trade.id)}`
}

export function getTradeItemImage(item?: TradeItem | null): string | null {
  if (!item) return null
  const productImage = item.product?.imageUrls?.[0]
  if (productImage) return productImage
  const cardImage = item.card?.imageUrl
  if (Array.isArray(cardImage) && cardImage.length > 0) return cardImage[0]
  if (typeof cardImage === 'string' && cardImage) return cardImage
  if (item.imageUrl) return item.imageUrl
  return null
}

/** Filter customer search hits so the active field matches what the user is typing. */
export function filterCustomerSuggestionsByField(
  field: 'name' | 'email' | 'phone',
  query: string,
  list: import('./types').CustomerSuggestion[],
): import('./types').CustomerSuggestion[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  return list.filter((s) => {
    if (field === 'name') return s.customerName.toLowerCase().includes(q)
    if (field === 'email') return (s.customerEmail || '').toLowerCase().includes(q)
    const phoneHay = (s.customerPhone || '').toLowerCase()
    const digitsNeedle = q.replace(/\D/g, '')
    const digitsHay = (s.customerPhone || '').replace(/\D/g, '')
    return phoneHay.includes(q) || (digitsNeedle.length > 0 && digitsHay.includes(digitsNeedle))
  })
}

/** Build POST /trades/:id/items payload from an existing line (undo restore). */
export function tradeItemToAddPayload(item: import('./types').TradeItem): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    itemName: item.itemName,
    itemSku: item.itemSku,
    setName: item.setName,
    setCode: item.setCode,
    cardNumber: item.cardNumber,
    condition: item.condition,
    printing: item.printing,
    quantity: item.quantity,
    valueCents: item.valueCents,
    imageUrl: item.imageUrl ?? null,
    metadata: item.metadata ?? undefined,
    side: item.side,
  }
  if (item.productId) payload.productId = item.productId
  if (item.cardId) payload.cardId = item.cardId
  return payload
}
